import React, { useState } from 'react';
import { resetPassword } from '@aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--surface);
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 600px;
  background: url('/images/hero-2.jpeg') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    padding: 0 var(--spacing-md);
    min-height: 100vh;
    text-align: center;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  color: white;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: var(--spacing-xl);
  }
`;

const ResetCard = styled.div`
  position: relative;
  z-index: 2;
  width: 400px;
  background: rgba(255, 255, 255, 0.95);
  padding: var(--spacing-xl);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    padding: var(--spacing-lg);
  }
  
  @media (max-width: 480px) {
    padding: var(--spacing-md);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: var(--spacing-sm);
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--primary-dark);
  }
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--error);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const SuccessMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--success);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const BackLink = styled(Link)`
  color: var(--text-secondary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  function getCognitoErrorCode(error) {
    if (error?.code) return error.code;
    if (error?.name) return error.name;
    if (error?.['x-amzn-errortype']) return error['x-amzn-errortype'];
    return '';
  }

  function getCognitoErrorMessage(error) {
    if (error?.message) return error.message;
    if (error?.['x-amzn-errormessage']) return error['x-amzn-errormessage'];
    return '';
  }

  function isUserNotFoundError(error) {
    const code = getCognitoErrorCode(error).toLowerCase();
    const msg = getCognitoErrorMessage(error).toLowerCase();
    if (code === 'usernotfoundexception') return true;
    if (msg.includes('username/client id combination not found')) return true;
    if (msg.includes('user not found')) return true;
    return false;
  }

  function mapResetPasswordErrorMessage(error) {
    if (isUserNotFoundError(error)) {
      return 'No account found with this email address. Please check your email or create a new account.';
    }
    return 'Failed to send reset code. Please try again or contact support.';
  }

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    try {
      await resetPassword({ username: email });
      setSuccess(true);
      navigate('/confirm-reset-password', { state: { email } });
    } catch (error) {
      console.error('Error initiating password reset:', error.message);
      setError(mapResetPasswordErrorMessage(error));
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <h1>Empowering Young Minds Through Continuous Learning</h1>
          <p>Join thousands of students achieving their academic goals with BrainyHoot's 360° learning experience.</p>
        </HeroContent>
        
        <ResetCard>
          <h2 style={{ marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>
            Reset Password
          </h2>
          
          <p style={{ 
            color: 'var(--text-secondary)', 
            textAlign: 'center',
            marginBottom: 'var(--spacing-lg)'
          }}>
            Enter your email address and we'll send you a code to reset your password.
          </p>

          <Form onSubmit={handleResetPassword}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>Reset code sent! Check your email.</SuccessMessage>}

            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <Button type="submit">
              Send Reset Code
            </Button>

            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)' }}>
              <BackLink to="/signin">
                ← Back to Sign In
              </BackLink>
            </div>
          </Form>
        </ResetCard>
      </HeroSection>
    </Container>
  );
};

export default ResetPassword; 